import { forwardRef, useEffect, useRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import cn from "../../../utils/cn";
import autosize from 'autosize';
import { isEmpty } from 'lodash';
import { fieldProps, getPreferredProp } from "../utils";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const TextField = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    id,
    label,
    afterLabelContent = null,
    ariaLabel: controlledAriaLabel = null,
    autoFocus = false,
    className = "",
    disabled = false,
    hideLabel = false,
    hint = "",
    inline = false,
    labelClassName = "",
    placeholder = "",
    required = false,
    cols = 50,
    rows = 4,
    style = {
      container: {},
      label: {},
      text: {},
      hint: {},
      error: {}
    },
    maxLength = null
  } = props;
  const inputRef = useRef(null);
  useImperativeHandle(ref, () => ({
    props
  }));
  useEffect(() => {
    if (inputRef.current) {
      autosize(inputRef.current);
    }
    return () => {
      if (inputRef.current) {
        autosize.destroy(inputRef.current);
      }
    };
  }, []);
  const hasError = getPreferredProp(props, "invalid") && getPreferredProp(props, "touched");
  const error = getPreferredProp(props, "error") || (required ? "Required" : "");
  const containerClass = () => cn({
    'mb-2.5': true,
    'pt-1.5': inline,
    'flex flex-row flex-nowrap': inline,
    'opacity-50': disabled
  }, className);
  const labelClass = () => cn({
    'flex-none text-right mr-2.5 pt-3 capitalize text-dark-grey text-xs tracking-normal font-semibold leading-4 text-action-blue': true,
    'pt-1.5': inline,
    'text-red': hasError,
    'text-dark-grey': !hasError,
    [labelClassName]: !isEmpty(labelClassName),
    'sr-only': hideLabel,
    'app-components-form-field__label--required': required
  });
  const textAreaWrapperClass = () => cn({
    'w-full': true
  });
  const textAreaClass = () => cn({
    'bg-white rounded border-solid border box-border text-sm text-text-blue font-regular-font leading-5 p-2 w-full min-h-50px overflow-hidden resize-y-important break-words': true,
    'border-red': hasError,
    'border-light-border-blue': !hasError,
    'opacity-50': disabled,
    'hover:bg-medium-fill-grey': !disabled
  });
  const errorClass = () => cn({
    'text-red text-xs h-4': true,
    hidden: !hasError,
    visible: hasError
  });
  const ariaLabel = typeof (label || placeholder) === "string" ? (label || placeholder).toLowerCase() : "";
  return /*#__PURE__*/_jsxs("div", {
    className: containerClass(),
    style: style.container,
    "aria-label": hasError ? `${ariaLabel} ${getPreferredProp(props, "error")}` : ariaLabel,
    children: [/*#__PURE__*/_jsx("label", {
      htmlFor: id,
      className: labelClass(),
      style: style.label,
      children: label
    }), afterLabelContent && /*#__PURE__*/_jsx("div", {
      className: "mb-4 mt-2 font-regular-font text-dark-grey",
      children: afterLabelContent
    }), /*#__PURE__*/_jsxs("div", {
      className: textAreaWrapperClass(),
      children: [/*#__PURE__*/_jsx("textarea", {
        className: textAreaClass(),
        id: id,
        value: getPreferredProp(props, "value"),
        onChange: getPreferredProp(props, "onChange"),
        onBlur: getPreferredProp(props, "onBlur"),
        onFocus: getPreferredProp(props, "onFocus"),
        placeholder: placeholder,
        name: getPreferredProp(props, "name"),
        disabled: disabled,
        autoFocus: autoFocus,
        style: {
          resize: "vertical !important"
        },
        cols: cols,
        rows: rows,
        ref: inputRef,
        maxLength: maxLength
      }), /*#__PURE__*/_jsx("div", {
        className: "mt-0.5 text-dark-grey text-xs italic leading-4",
        style: style.hint,
        children: hint
      }), /*#__PURE__*/_jsx("div", {
        className: errorClass(),
        style: style.error,
        children: error
      })]
    })]
  });
});
TextField.propTypes = {
  /** id for reference */
  id: PropTypes.string.isRequired,
  /** className for reference */
  className: PropTypes.string,
  /**
   * Override the inline-styles of the elements || **Not Recommended**
   * <br> ***note: currently overwritten by mysterious inline styles in `textarea`***
   */
  style: PropTypes.shape({
    container: PropTypes.object,
    label: PropTypes.object,
    text: PropTypes.object,
    hint: PropTypes.object,
    error: PropTypes.object
  }),
  /** label text for button */
  label: PropTypes.node.isRequired,
  /** insert content after label */
  afterLabelContent: PropTypes.node,
  /** Should the label be displayed inline with the input? */
  inline: PropTypes.bool,
  /** Is the input disabled? */
  disabled: PropTypes.bool,
  /** Placeholder text */
  placeholder: PropTypes.string,
  /** Should the label be hidden? */
  hideLabel: PropTypes.bool,
  /** Should the UUtextfield be focused on load? */
  autoFocus: PropTypes.bool,
  /** Help text displayed below the input */
  hint: PropTypes.node,
  /** Is the field required */
  required: PropTypes.bool,
  /** number of columns for text area */
  cols: PropTypes.number,
  /** number of rows for text area */
  rows: PropTypes.number,
  /** max characters for text area */
  maxLength: PropTypes.number,
  /** See fieldProps doc */
  ...fieldProps
};
TextField.displayName = 'TextField';
export default TextField;
//# sourceMappingURL=TextField.js.map